import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { FormProvider } from 'react-hook-form';
import { FormLayout, FormRow as Row, FormSection as Section } from '~/components';
import { ButtonFooter } from '~/components/buttons/ButtonFooter';
import { Select, TextInput } from '../../../components';
import { COUNTRIES } from '../../../constants';
import { useCustomForm } from '../../../hooks/useCustomForm';
import { FacilityFormValues, facilitySchemaRequiredNoAddress } from '../../types';
import { emptyFacilityForm } from './constants';
import { StateOrProvinceInput } from './StateOrProvinceInput';

const TAGS = CT.facility.form;

interface Props {
  facilityForm: Partial<FacilityFormValues>;
  handleFacilityFormSubmit: (values: FacilityFormValues) => void;
}

export const FacilityForm = (props: Props) => {
  const { facilityForm, handleFacilityFormSubmit } = props;

  const methods = useCustomForm<FacilityFormValues>({
    resolver: zodResolver(facilitySchemaRequiredNoAddress),
    defaultValues: { ...emptyFacilityForm, ...facilityForm, userAdded: true },
  });

  const { handleSubmit } = methods;

  const onSubmitSuccess = (values: FacilityFormValues) => {
    handleFacilityFormSubmit(values);
  };

  const onSubmitError = (err: unknown) => console.debug('Error submitting facility form: ', err);

  return (
    <FormProvider {...methods}>
      <form data-cy={TAGS.form} onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}>
        <FormLayout>
          <Section componentsPerLine={1}>
            <Row>
              <TextInput name={'label'} cyTag={TAGS.name} label={'Facility Name*'} />
            </Row>
          </Section>
          <Section componentsPerLine={1}>
            <Row>
              <TextInput
                name={'address.addressLine1'}
                autoComplete={'street address address-line1'}
                placeholder="Street Address..."
                cyTag={TAGS.addressLine1}
                label={'Address Line 1'}
              />
            </Row>
            <Row>
              <TextInput
                name={'address.addressLine2'}
                autoComplete={'street address address-line2'}
                cyTag={TAGS.addressLine2}
                label={'Address Line 2'}
              />
            </Row>
          </Section>
          <Section componentsPerLine={2}>
            <Row>
              <TextInput
                name={'address.city'}
                autoComplete={'city'}
                placeholder="City"
                cyTag={TAGS.cityInput}
                label={'City'}
              />
            </Row>
            <Row>
              <StateOrProvinceInput
                name={'address.stateCode'}
                countryFieldName={'address.countryCode'}
                cyTag={TAGS.stateSelect}
              />
            </Row>
            <Row>
              <Select
                name={'address.countryCode'}
                label={'Country'}
                cyTag={TAGS.countrySelect}
                options={COUNTRIES}
              />
            </Row>
            <Row>
              <TextInput
                name={'address.postalCode'}
                cyTag={TAGS.postalInput}
                label={'Postal Code'}
              />
            </Row>
          </Section>
          <ButtonFooter primaryButton={{ label: 'Save', cyTag: TAGS.submitBtn, isSubmit: true }} />
        </FormLayout>
      </form>
    </FormProvider>
  );
};
